import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { formatAmount, formatDateOnly, getFormatConfig } from '../../../utils/global';
import { downloadPaymentBatchExcel } from './paymentBatchExcelExport';
import { loadPaymentBatchDetail } from './paymentBatchListUtils';
import { fetchDuplicateWarnings, warningLabel, warningsForPaymentIds } from './paymentDuplicateWarning';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogCloseIconSx,
  harmonateSectionTitleSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getTenantId = () => localStorage.getItem('tenant_id') || '';

const PaymentBatchDetailDialog = ({
  open,
  onClose,
  batch,
  businessUnitName,
}) => {
  const tenantId = getTenantId();
  const config = getFormatConfig();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);

  useEffect(() => {
    if (!open || !batch?.paymentBatchId || !tenantId) {
      setDetail(null);
      return undefined;
    }

    let active = true;
    setLoading(true);
    Promise.all([
      loadPaymentBatchDetail(tenantId, batch),
      fetchDuplicateWarnings(tenantId),
    ])
      .then(([loaded, warnings]) => {
        if (!active) return;
        setDetail(loaded);
        setDuplicateWarnings(warnings);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, batch, tenantId]);

  const bankCount = batch?.banks?.length || 0;
  const vendorCount = useMemo(() => {
    const names = new Set();
    (batch?.banks || []).forEach((bank) => {
      (bank.vendors || []).forEach((vendor) => {
        if (vendor.vendorName) names.add(vendor.vendorName);
      });
    });
    return names.size;
  }, [batch]);

  const { canExport } = useFunctionPermissions(FC.PAYMENT_INTELLIGENCE);
  const handleExport = () => {
    if (!canExport || !batch || !detail) return;

    downloadPaymentBatchExcel({
      batch: {
        batchNumber: batch.batchNumber,
        paymentDate: batch.paymentDate,
        bankName: bankCount === 1 ? detail.resolveBankName(batch.banks?.[0]?.bankId) : 'Multiple',
        paymentMethod: batch.paymentMethod || '',
        totalAmount: batch.totalAmount,
        batchStatus: batch.batchStatus,
        bankCount,
        vendorCount,
      },
      bankSummary: detail.bankSummary,
      payments: detail.paymentLines.map((line) => ({
        invoiceNumber: line.invoiceNumber,
        vendorName: line.vendorName,
        amount: '',
        paidAmount: '',
        paymentAmount: line.paymentAmount,
        remainingBalance: '',
        bankName: line.bankName,
        paymentMethod: line.paymentMethod,
        paymentReference: line.paymentReference,
      })),
      businessUnitName,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
      <DialogTitle sx={harmonateDialogTitleSx}>
        Payment Batch — {batch?.batchNumber || '—'}
        <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {batch && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Payment Date</Typography>
              <Typography variant="body2">{batch.paymentDate ? formatDateOnly(batch.paymentDate, config.DateFormat) : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Typography variant="body2">{batch.batchStatus || 'DRAFT'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Total Amount</Typography>
              <Typography variant="body2">{formatAmount(batch.totalAmount, config.AmountFormat)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Banks / Vendors</Typography>
              <Typography variant="body2">{bankCount} bank{bankCount === 1 ? '' : 's'}, {vendorCount} vendor{vendorCount === 1 ? '' : 's'}</Typography>
            </Box>
          </Box>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && detail && (
          <>
            <Box>
              <Typography variant="subtitle2" sx={{ ...harmonateSectionTitleSx, mb: 1 }}>Vendor & Bank Summary</Typography>
              <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Bank</TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell align="right">Payments</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.vendorBankGroups.map((vendor) => (
                      vendor.bankRows.map((row, index) => (
                        <TableRow key={`${vendor.vendorName}-${row.bankName}-${index}`}>
                          {index === 0 && (
                            <TableCell
                              rowSpan={vendor.bankRows.length}
                              sx={{ fontWeight: 600, verticalAlign: 'middle', borderRight: '1px solid', borderColor: 'divider' }}
                            >
                              {vendor.vendorName}
                            </TableCell>
                          )}
                          <TableCell>{row.bankName || '—'}</TableCell>
                          <TableCell>{row.paymentMethods || '—'}</TableCell>
                          <TableCell align="right">{row.paymentCount}</TableCell>
                          <TableCell align="right">{formatAmount(row.totalAmount, config.AmountFormat)}</TableCell>
                        </TableRow>
                      ))
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ ...harmonateSectionTitleSx, mb: 1 }}>Payments</Typography>
              <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Bank</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Duplicate</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.paymentLines.map((line) => (
                      <TableRow key={line.paymentId}>
                        <TableCell>{line.invoiceNumber}</TableCell>
                        <TableCell>{line.vendorName}</TableCell>
                        <TableCell>{line.bankName || '—'}</TableCell>
                        <TableCell>{line.paymentMethod}</TableCell>
                        <TableCell>{line.paymentStatus}</TableCell>
                        <TableCell>
                          {warningsForPaymentIds(duplicateWarnings, [line.paymentId]).map((warning) => (
                            <Typography key={warning.matchedPaymentId} variant="caption" display="block">
                              {warningLabel(warning)}
                              {warning.matchedInvoiceNumber ? ` — ${warning.matchedInvoiceNumber}` : ''}
                            </Typography>
                          ))}
                        </TableCell>
                        <TableCell align="right">{formatAmount(line.paymentAmount, config.AmountFormat)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={harmonateCancelButtonSx}>Close</Button>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={!detail?.paymentLines?.length || !canExport}
          sx={harmonateUploadOutlinedSx}
        >
          Export Excel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentBatchDetailDialog;
